import { useTranslation } from 'react-i18next'; // Import hook for translation (i18n)
import { useDispatch, useSelector } from 'react-redux'; // Import hooks to interact with Redux store
import { useNavigate } from 'react-router-dom'; // Import hook for navigation
import { useEffect, useLayoutEffect, useState } from 'react'; // Import React hooks for side effects and state management
import { toast } from 'react-toastify'; // Import toast notifications for user feedback
import {createNormalEvent, GetEventSetting} from '../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi'; // Import API actions
import routes from '../../../../../Routes/routes'; // Import route definitions
import { errorMsg, getDateWithFormat } from '../../../../../Apis/functions';
import { USER_TYPE_MODEL } from '../../../../../enums/userTypeEnum';
import HandleChange from '../../../../../Helpers/HandleChange';
import { RESERVATION_MODEL, STEPS_CREATE_RESERVATIONS } from '../Model/Models';


/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const CreateEventApp = () => {
    const { t } = useTranslation(); // Translation function to handle multi-language support
    const dispatch = useDispatch(); // Dispatch function for Redux actions
    const navigate = useNavigate(); // Navigation function for redirecting to different routes

    const userData = useSelector((state) => state?.authenticatioauthennSlice?.user.data); // Get current user data from Redux store
    const companyRestrictionsData = useSelector((state) => state?.EmployeeEventsSlice?.companyRestrictionsData); // Get company restrictions data

    const [selectedHost, setSelectedHost] = useState([]);
    const [eventDetail, setEventDetail] = useState({ ...RESERVATION_MODEL });
    const [usersSelected, setUsersSelected] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);
    const [steps, setSteps] = useState([]); // State for step titles in the event creation process
    const [activeStep, setActiveStep] = useState(0); // State to track active step in the process
    const { handleChange } = HandleChange({ model: eventDetail, setModel: setEventDetail });
    const [modified, setModified] = useState(false);

    useEffect(() => {
        dispatch(GetEventSetting());
    }, []);

    // Set up the steps for the event creation process
    useLayoutEffect(() => {
        setSteps([t(STEPS_CREATE_RESERVATIONS.DETAILS.TRANS), t(STEPS_CREATE_RESERVATIONS.SCHEDULE_LOCATION.TRANS), t(STEPS_CREATE_RESERVATIONS.ATTENDEES_TYPES.TRANS), t(STEPS_CREATE_RESERVATIONS.SUMMARY.TRANS)]);
    }, [t]);

    /**
     * Function to create the reservation
     */
    const handleCreate = () => {
        let separateTime = eventDetail?.bookingSelected?.from.split(':');
        const startDate = eventDetail?.dateSelected?.setHours(separateTime[0], separateTime[1], 0, 0);

        const employeeList = usersSelected
            .filter(user => user.userTypeId !== USER_TYPE_MODEL.VISITOR)
            .map(user => ({ id: user.userId }));

        const visitorList = usersSelected
            .filter(user => user.userTypeId === USER_TYPE_MODEL.VISITOR)
            .map(user => ({
                id: (user?.id || user?.userId),
                visitorType: {
                    id: user.visitorTypeId
                }
            }));

        const body = {
            name: eventDetail?.name,
            hostId: selectedHost?.length > 0 ? selectedHost[0].userId : userData?.id,
            startDate: startDate,
            reservationZoneId: eventDetail?.zoneSelected?.zoneId,
            visitPurpose: 'No comments.',
            duration: eventDetail?.bookingSelected?.duration,
            reservationDate: getDateWithFormat(eventDetail?.dateSelected),
            reservationFromTime: eventDetail?.bookingSelected?.from,
            reservationToTime: eventDetail?.bookingSelected?.to,
            employees: employeeList,
            visitors: visitorList,
        };

        dispatch(createNormalEvent(body)).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                toast.success(t('has_been_created_successfully'));
                navigate(routes.event.list);
            } else {
                errorMsg(code);
            }
        });
    }

    // Redirect if company restrictions apply (e.g., if it's a restricted event)
    useEffect(() => {
        if (companyRestrictionsData?.isOnuEvent === true) {
            navigate(routes.unauthorized); // Navigate to the unauthorized route
        }
    });

    /**
     * Function for changing steps
     * @param step
     */
    const handleStepChange = (step) => {
        setActiveStep(step);
    };

    /**
     * Function to remove form fields from steps
     * @param step: number
     */
    const handleClearSteps = (step) => {
        switch (step) {
            case STEPS_CREATE_RESERVATIONS.DETAILS.INDEX:
                setEventDetail(prev => ({
                    ...prev,
                    name: null,
                    isHost: true,
                }));
                setSelectedHost([]);
                break;

            case STEPS_CREATE_RESERVATIONS.SCHEDULE_LOCATION.INDEX:
                setEventDetail(prev => ({
                    ...prev,
                    zoneSelected: null,
                    dateSelected: null,
                    bookingSelected: null,
                }));
                break;

            case STEPS_CREATE_RESERVATIONS.ATTENDEES_TYPES.INDEX:
                setUsersSelected([]);
                break;

            default:
                break;
        }
    };

    /**
     * Function that validates the required fields according to the step obtained from the parameter
     * @param step: number
     * @returns {boolean}
     */
    const handleValidateSteps = (step) => {
        const showWarning = () => toast.warning(t("please_fill_all_fields"));

        switch (step) {
            case STEPS_CREATE_RESERVATIONS.DETAILS.INDEX: {
                const isValid = eventDetail.isValidStepDetails();
                const hasHost = eventDetail.isHost === true || (eventDetail.isHost === false && selectedHost.length > 0);
                if (!isValid || !hasHost) {
                    setEmptyFields(true);
                    return false;
                }
                return true;
            }
            case STEPS_CREATE_RESERVATIONS.SCHEDULE_LOCATION.INDEX: {
                const isValidLocation = eventDetail.isValidStepLocation();
                if (!isValidLocation) {
                    setEmptyFields(true);
                    showWarning();
                    return false;
                }
                return true;
            }
            case STEPS_CREATE_RESERVATIONS.ATTENDEES_TYPES.INDEX: {
                const hasUsers = usersSelected.length > 0;
                if (!hasUsers) {
                    setEmptyFields(true);
                    showWarning();
                    return false;
                }
                return true;
            }
            default:
                return true;
        }
    };


    return {
        t,
        steps,
        activeStep,
        setActiveStep,
        eventDetail,
        setEventDetail,
        selectedHost, setSelectedHost,
        handleChange,
        usersSelected,
        setUsersSelected,
        emptyFields,
        setEmptyFields,
        userData,
        modified,
        setModified,
        handleStepChange,
        handleCreate,
        handleClearSteps,
        handleValidateSteps
    };
};

export default CreateEventApp;
